import { FC } from 'react';
import Link from 'next/link';
import classNames from 'classnames';

import { GProduct, Variant } from '@/types';

import { formatWeightLabel } from '../Counter/Utils';
import styles from './weigthvariants.module.scss';
import { Button } from '../Button';

import { currencyFormat } from '@/utils';
import { useFeaturedFlag, useProducts } from '@/hooks';
import { useProductContext } from '../Product/context';

interface IWeightVariants {
  currentWeightVariant: Variant;
  product: GProduct;
  handleWeightVariatChange: (_: Variant) => void;
  inKiosk?: boolean;
}

const WeightVariants: FC<IWeightVariants> = ({
  currentWeightVariant,
  handleWeightVariatChange,
  product,
  inKiosk = false,
}) => {
  const { products } = useProducts();
  const { showStriketroughPrice } = useFeaturedFlag();
  const {
    unitPricingTiers,
    unitPricingTier,
    updateUnitPricingTier,
    setSelectedProductId,
    selectedProductId,
  } = useProductContext();
  const hasUnitTier = product.pricing?.tier_method === 'UNIT';
  const hasSiblings = (product?.siblings?.length ?? 0) > 1;

  const variantActives = products?.reduce((result, current) => {
    if (current?.id !== product.id) {
      return result;
    }
    return `${current?.weightVariant?.id},${result}`;
  }, '');

  return (
    <>
      <p className={styles.counter__weight_variant_title_weights}>
        Available variants
      </p>
      {hasSiblings ? (
        <div className={styles.counter__weight_variant_container}>
          {product?.siblings
            ?.filter(sibling => !!sibling?.path_slug)
            ?.map(sibling => {
              const isActive = inKiosk
                ? sibling?.id === selectedProductId
                : sibling?.id === product?.id;
              const variant = sibling?.variants?.[0];
              const size = sibling?.size;

              const priceLabel = currencyFormat(variant?.salePrice!);
              const discountLabel = currencyFormat(variant?.price!);

              const weight = formatWeightLabel(variant!);

              const label = weight?.endsWith('G')
                ? `${weight.split('G')[0]} gram`
                : weight || size;

              const content = (
                <div className={styles.weight_variant_btn_content}>
                  <span className={styles.weight_variant_btn_label}>
                    {label}
                  </span>
                  <div className={styles.weight_variant_btn_price_container}>
                    {priceLabel ? (
                      <span
                        className={classNames(styles.weight_variant_btn_price, {
                          [styles.weight_variant_btn_price_active]: isActive,
                        })}
                      >
                        ${priceLabel}
                      </span>
                    ) : null}
                    {showStriketroughPrice &&
                      !(variant?.price === variant?.salePrice) && (
                        <span className={styles.weight_variant_btn_discount}>
                          {discountLabel}
                        </span>
                      )}
                  </div>
                </div>
              );

              if (inKiosk) {
                return (
                  <Button
                    ariaLabel={label}
                    className={classNames(styles.weight_variant_btn, {
                      [styles.weight_variant_btn_active]: isActive,
                    })}
                    key={sibling?.id ?? sibling?.path_slug}
                    onClick={() =>
                      sibling?.id && setSelectedProductId(sibling.id)
                    }
                  >
                    {content}
                  </Button>
                );
              }

              return (
                <Link
                  className={classNames(styles.weight_variant_btn, {
                    [styles.weight_variant_btn_active]: isActive,
                  })}
                  href={sibling?.path_slug!}
                  key={sibling?.id ?? sibling?.path_slug}
                  scroll={false}
                >
                  {content}
                </Link>
              );
            })}
        </div>
      ) : hasUnitTier ? (
        <div className={styles.counter__weight_variant_container}>
          {unitPricingTiers?.map((tier, index) => {
            const price = tier.price! * tier.qty;
            const priceLabel = currencyFormat(price! * 100);

            const salePrice = price! - (product.pricing?.discount_amount ?? 0);
            const salePriceLabel = currencyFormat(salePrice! * 100);

            const label = `Buy ${tier.qty}`;
            const isActive =
              unitPricingTier?.qty === tier.qty &&
              unitPricingTier?.price === tier.price;

            return (
              <Button
                ariaLabel="label"
                className={classNames(styles.weight_variant_btn, {
                  [styles.weight_variant_btn_active]: isActive,
                })}
                key={index}
                onClick={() => updateUnitPricingTier(tier)}
              >
                <div className={styles.weight_variant_btn_content}>
                  <span className={styles.weight_variant_btn_label}>
                    {label}
                  </span>
                  <div className={styles.weight_variant_btn_price_container}>
                    <span
                      className={classNames(styles.weight_variant_btn_price, {
                        [styles.weight_variant_btn_price_active]: isActive,
                      })}
                    >
                      ${salePriceLabel}
                    </span>
                    {showStriketroughPrice && !(price === salePrice) && (
                      <span className={styles.weight_variant_btn_discount}>
                        ${priceLabel}
                      </span>
                    )}
                  </div>
                </div>
              </Button>
            );
          })}
        </div>
      ) : (
        <div className={styles.counter__weight_variant_container}>
          {product?.variants?.map(variant => {
            const id = variant?.id!;
            const priceLabel = currencyFormat(variant?.salePrice!);
            const discountLabel = currencyFormat(variant?.price!);

            const weight = formatWeightLabel(variant!);

            const label = weight?.endsWith('G')
              ? `${weight.split('G')[0]} gram`
              : weight;

            return (
              <Button
                ariaLabel="label"
                className={classNames(styles.weight_variant_btn, {
                  [styles.weight_variant_btn_active]:
                    currentWeightVariant?.id == id ||
                    variantActives.includes(`${id},`),
                })}
                key={id}
                onClick={() => handleWeightVariatChange(variant!)}
              >
                <div className={styles.weight_variant_btn_content}>
                  <span className={styles.weight_variant_btn_label}>
                    {label}
                  </span>
                  <div className={styles.weight_variant_btn_price_container}>
                    <span
                      className={classNames(styles.weight_variant_btn_price, {
                        [styles.weight_variant_btn_price_active]:
                          currentWeightVariant?.id == id ||
                          variantActives.includes(`${id},`),
                      })}
                    >
                      ${priceLabel}
                    </span>
                    {showStriketroughPrice &&
                      !(variant?.price === variant?.salePrice) && (
                        <span className={styles.weight_variant_btn_discount}>
                          {discountLabel}
                        </span>
                      )}
                  </div>
                </div>
              </Button>
            );
          })}
        </div>
      )}
    </>
  );
};

export default WeightVariants;
